<template>
  <div :id="validModalId" class="modal fade" tabindex="-1" role="dialog" :data-test="dataTest">
    <div id="" :style="additionalStyle" :class="'modal-dialog modal-' + size" role="document">
      <div class="modal-content">
        <div class="modal-header text-left">
          <button type="button" class="close" aria-label="Close" @click="cancel">
            <span aria-hidden="true">&times;</span>
          </button>
          <div class="topright pull-right m-r-25" style="margin-top: -8px">
            <slot name="topright" />
          </div>
          <slot name="header">
            <h4>{{ headerText }}</h4>
          </slot>
        </div>
        <div :class="bodyClass">
          <div>
            <slot />
          </div>
        </div>
        <div v-if="removeFooter == false" class="modal-footer">
          <slot name="button-instructions"></slot>
          <div class="tw-flex tw-justify-between tw-w-full tw-items-center">
            <div>
              <button
                v-if="!removeCancelButton"
                :disabled="disableCancel"
                type="button"
                class="cancel-button btn btn-default"
                :data-test="dataTest + '-cancel'"
                @click.prevent="negativeResponse"
              >
                {{ disableTranslations ? cancelButtonText : $t(cancelButtonText) }}
              </button>
            </div>

            <slot name="additionalButtons" />

            <div v-if="!removeConfirmButton">
              <PopperIconHelp
                v-if="$slots['help-content']"
                default-slot-is-inline
                class="inline text-left"
              >
                <slot name="help-content"></slot>
              </PopperIconHelp>
              <a
                v-if="confirmButtonIsHref && !disableSubmit"
                :href="confirmButtonHref"
                :class="confirmButtonCssClass"
                :data-test="dataTest + '-confirm-href'"
              >
                {{ disableTranslations ? actionButtonText : $t(actionButtonText) }}
              </a>

              <button
                v-else
                :disabled="disableSubmit"
                :class="confirmButtonCssClass"
                class="confirm-button inline"
                :data-test="dataTest + '-confirm-button'"
                @click.prevent="positiveResponse"
              >
                {{ disableTranslations ? actionButtonText : $t(actionButtonText) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import { ModalSaveState } from '@/utils/enum'
import PopperIconHelp from '@/components/PopperIconHelp.vue'
import { validateSizeName } from '@/utils/propValidation'
/**
 * Component can be used to perform actions, to allow following a link, or simply display info/content.
 *
 * Specs on events:
 *
 * Clicking the cancel button will emit a 'negative-response' event only
 * Clicking outside of modal to close, or on the 'x' button, will emit a 'cancel' event only
 * Clicking the save button will emit 'positive-response' AND a 'save' event
 *
 * note: due to the naming of cancelButton, it may be confusing that a 'cancel' event is not emitted
 *       when clicking it, we should change this naming to negativeResponseButton to avoid
 *       that confusion. An actual cancellation of intention is exiting the modal without
 *       doing anything which is why, in that scenario, we emit the 'cancel' event
 *
 * @todo We need to unify language 'confirm', 'positive', 'save' to make it easier to understand
 * @todo Refactor to need fewer props
 */
export default {
  components: { PopperIconHelp },
  props: {
    modalId: { type: String, default: 'editmodal' },
    saveState: { type: String, default: 'fresh' },
    headerText: { type: String, default: 'Edit' },
    cancelButtonText: { type: String, default: 'Cancel' },
    confirmButtonText: { type: String, default: 'Save' },
    confirmButtonClass: { type: String, default: '' },
    isOpen: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    disableTranslations: { type: Boolean, default: false },
    removeCancelButton: { type: Boolean, default: false },
    removeConfirmButton: { type: Boolean, default: false },
    removeFooter: { type: Boolean, default: false },
    size: { validator: validateSizeName, default: 'md' },
    bodyClass: { type: String, default: 'modal-body' },
    noTopMargin: { type: Boolean, default: false },
    canRetry: { type: Boolean, default: false },
    savingText: { type: String, default: 'Saving...' },
    savedText: { type: String, default: 'Saved!' },
    failedText: { type: String, default: 'Failed' },
    confirmButtonIsHref: { type: Boolean, default: false },
    confirmButtonHref: { type: String, default: '' },
    dataTest: { type: String, default: 'base-modal' },
  },
  data() {
    return {
      saveButtonAttempted: false,
      cancelButtonAttempted: false,
      canTriggerCancelActions: true,
    }
  },
  computed: {
    disableSubmit() {
      return (
        (this.disabled ||
          this.saveState === ModalSaveState.SAVING ||
          this.saveState === ModalSaveState.FAILED ||
          this.saveState === ModalSaveState.SAVED) &&
        !this.canRetry
      )
    },
    disableCancel() {
      return (
        (this.saveState === ModalSaveState.SAVING ||
          this.saveState === ModalSaveState.FAILED ||
          this.saveState === ModalSaveState.SAVED) &&
        !this.canRetry
      )
    },
    actionButtonText() {
      switch (this.saveState) {
        case ModalSaveState.SAVING:
          return this.savingText
        case ModalSaveState.SAVED:
          return this.savedText
        case ModalSaveState.FAILED:
          return this.failedText
        default:
          return this.confirmButtonText
      }
    },
    confirmButtonCssClass() {
      return this.confirmButtonClass ? this.confirmButtonClass : 'btn btn-primary btn-block'
    },
    additionalStyle() {
      return this.noTopMargin ? { 'margin-top': 0 } : {}
    },
    validModalId() {
      return this.modalId.replace(/^[^a-z]+|[^\w:.-]+/gi, '')
    },
  },
  watch: {
    isOpen(newIsOpen) {
      if (newIsOpen) {
        this.openModalHandler()
      } else {
        this.closeModal()
      }
    },
  },
  created() {
    if (this.isOpen) {
      nextTick(() => {
        this.openModalHandler()
      })
    }
  },
  methods: {
    $t(key) {
      return $t(key)
    },
    openModalHandler() {
      // The next tick helps handle situations where the id changes in the same draw iteration as us trying to show the
      // modal
      this.$nextTick(() => {
        $(`#${this.validModalId}`)
          .on('hide.bs.modal', (evt) => {
            if (this.canTriggerCancelActions) {
              this.cancel()
            }
            this.canTriggerCancelActions = true
            $(`#${this.validModalId}`).off(evt) // Stops us from adding too many events
          })
          .on('show.bs.modal', (evt) => {
            this.saveButtonAttempted = false // reset modal state when opened
            this.$emit('opened')
            $(`#${this.validModalId}`).off(evt) // Stops us from spamming opened events
          })
          .modal('show')
      })
    },
    closeModal() {
      // Helps handle situations similar to the open modal where we change the id in the same phase as we try to hide a
      this.$nextTick(() => $(`#${this.validModalId}`).modal('hide'))
    },
    positiveResponse() {
      this.$emit('positive-response')
      this.save()
    },
    save() {
      if (this.canRetry) {
        this.saveButtonAttempted = true
      }
      this.canTriggerCancelActions = false
      this.$emit('save')
    },
    negativeResponse() {
      this.cancelButtonAttempted = true
      this.$emit('negative-response')
      this.cancel()
      this.cancelButtonAttempted = false
      this.closeModal()
    },
    cancel() {
      // emit 'cancel' when:
      // 2. when the modal 'x' is clicked to close it
      // 3. click outside the modal in order to close it
      // NOT when the "negative response" button is clicked
      // NOT when the "positive response" button is clicked
      if ((!this.saveButtonAttempted && !this.cancelButtonAttempted) || this.canRetry) {
        this.closeModal()
        this.$emit('cancel')
      }
    },
  },
}
</script>

<style scoped lang="scss"></style>
